import { SearchBox, Link, PrimaryButton, TextField, mergeStyles, Coachmark, TeachingBubbleContent, DirectionalHint, IPositioningContainerProps } from '@fluentui/react';
import * as React from 'react';
import { appContext } from '../../../../web/models/getHelp/appContext';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { getQueryFromDeepLink, getRandomGuidString } from '../../../../web/utils/getHelputils';
import { capturePageAction } from '../../../../web/utils/oneDsUtil';
import { Modality, AutoSuggestResult, AutoSuggestResultV2 } from '../../../models/apiModels';
import { AutomationIds } from '../../../models/constants/automationIds';
import { SMCExperimentQueryMaxCharacter, QueryMaxCharacter, AutoSuggestConstants, AutoSuggestV2Constants, SourceApps } from '../../../models/constants/constants';
import { getPullRightClassName, isSignUp, buildAutoSuggestContactUsFields } from '../../../utilities';
import { SelfHelpAutoSuggest } from '../presentationalComponents/SelfHelpAutoSuggest';
import { getCommonClassNames, getCommonStyles, getLinkStyles } from '../styles/commonStyles';
import { getContactUsClassNames, IContactUsStyles } from '../styles/contactUsStyles';
import { getGetHelpSearchBoxStyle, getHelpButtonStyles, getSearchBoxStyles } from '../styles/formQuerySubmitStyles';
import { IStyledComponent } from '../styles/themesUI';
import { getString, isRtl } from '../../../../web/utils/stringLoader';
import { collectTelemetry } from '../../../../web/utils/oneDsUtil';
import { LogType, LogHint } from '../../../../web/models/emeraldLoggingRequest';
import { IAppState } from '../../../models/reduxState';
import { store } from '../../../appStore';

interface FormProps {
  setOriginalQuery: (query: string) => any;
  formSubmission: (query: string) => any;
  dispatchFetchAutoSuggest: (query: string) => any;
  dispatchFetchAutoSuggestV2: (query: string) => any;
  dispatchAlchemyFetch: (searchText: string, actualQuery?: string, isSpecialQuery?: boolean, byPassPreProcess?: boolean) => any;
  initializeModalityStateOnGetHelp: (modalities: Modality[]) => any;
  articleClicked: (url: string) => any;
  setContactSearchedQueryText: (query: string, isAutoSuggestQuery: boolean) => any;
  setAutoSuggestFocusedIndex: (index: number) => void;
  showAutoSuggest: () => any;
  hideAutoSuggest: () => any;
  setIsAutoSuggestCardSearch: (isAutoSuggestCardSearch: boolean, autoSuggestResults: AutoSuggestResult[]) => void;
  setIsAutoSuggestCardSearchV2: (isAutoSuggestCardSearch: boolean, autoSuggestResultsV2: AutoSuggestResultV2) => void;
  clearRelatedCorrelationIds: () => void;
  setAutoSuggestResult: (autoSuggestResults: AutoSuggestResult[]) => any;
  setAutoSuggestResultV2: (autoSuggestResultsV2: AutoSuggestResultV2) => any;
  setVsbUpsellAlertActivated: () => void;
  setIsQueryChanged: (isQueryChanged: boolean) => void;
  hideCoachmark: () => void;
  hideRequestCreationTab: (isHidden: boolean) => void;
  autoSuggestFocusedIndex: number;
  runningInSoc: boolean;
  showCompactQueryForm: boolean;
  searchSubmitted: boolean;
  isAutoSuggestEnabled: boolean;
  isAutoSuggestV2Enabled: boolean;
  isAnyAutoSuggestEnabled: boolean;
  suggestions: string[] | AutoSuggestResult[];
  market: string;
  isAutoSuggestHidden: boolean;
  isAutoSuggestQuery: boolean;
  isAlchemyQueryInProgress: boolean;
  autoSuggestResults: AutoSuggestResult[];
  autoSuggestResultsV2: AutoSuggestResultV2;
  contactSearchedQuery: string;
  originalQuery: string;
  isDeepLinkEnabled: boolean;
  isDeepLinkQuery: boolean;
  isCreatingRequest: boolean;
  alchemyThemeRoutingEnabled: boolean;
  showSearchBar: boolean;
  isThirdParty: boolean;
  isTeams: boolean;
  isSurfaceApp: boolean;
  isGetHelp: boolean;
  isDarkTheme: boolean;
  deeplinkPath: string;
  isGetHelpBtnHidden: boolean;
  isPrivacyAndSupportHidden: boolean;
  shouldProceedQueryForGetHelp: boolean;
  isQueryChanged: boolean;
  isFeedbackCoachmarkEnabled: boolean;
  isFeedbackCoachmarkShowing: boolean;
}

interface AppState {
  coachmarkPosition: DirectionalHint;
  positioningContainerProps: IPositioningContainerProps;
}

type IFormQuerySubmitProps = FormProps & IStyledComponent<IContactUsStyles>;

export class FormQuerySubmit extends React.Component<IFormQuerySubmitProps, AppState> {
  public currentQuery: string;
  private splitMarket: string[] = this.props.market?.split('-');
  private language: string = (!!this.splitMarket && this.splitMarket.length > 0) ? this.splitMarket[0] : '';
  private nonWildcardLanguages: string[] = ['zh', 'ja', 'th'];
  private inputBoxRef: any;
  private maxCharacterCount: number = this.props.showCompactQueryForm ? SMCExperimentQueryMaxCharacter : QueryMaxCharacter;
  private charRemainingSpan: HTMLSpanElement;
  public constructor(props: IFormQuerySubmitProps) {
    super(props);
    this.inputBoxRef = React.createRef();
    const directionalHint = this.props.isFeedbackCoachmarkEnabled ? DirectionalHint.leftCenter : null;
    this.state = {

      coachmarkPosition: directionalHint,
      positioningContainerProps: this.getMemoizedPositioningContainerProps(directionalHint),
    };
  }

  public componentDidMount() {
    if (this.props.isGetHelp) {
      this.inputBoxRef?.current?.querySelector('input')?.focus();
    }
    if (this.props.shouldProceedQueryForGetHelp) {
      this.initiateGetHelpExperience(false, true, true);
    }

    if (this.props.isDeepLinkEnabled && this.props.isDeepLinkQuery) {
      this.charUpdate(this.props.contactSearchedQuery);
      if (!this.props.isGetHelpBtnHidden) {
        document.getElementById('getHelp').click();
      }
    }
  }

  public componentDidUpdate = (prevProps: FormProps & IStyledComponent<IContactUsStyles>, prevState: AppState): void => {
    if (this.props.isAlchemyQueryInProgress == false && prevProps.isAlchemyQueryInProgress == true && this.props.isGetHelp) {
      // In GetHelp commercial mode, the alchemy query is triggered directly without waiting for the configuration like consumer mode does.
      // which resulting in the input box not being focused properly, neither does the narrator read the input box content.
      // This is a workaround to focus the input box explictly again after some conditions are met.
      this.inputBoxRef?.current?.querySelector('input')?.focus();
    }
    if (
      (this.props.deeplinkPath !== prevProps.deeplinkPath) ||
      this.props.shouldProceedQueryForGetHelp !== prevProps.shouldProceedQueryForGetHelp
    ) {
      if (this.props.shouldProceedQueryForGetHelp) {
        this.initiateGetHelpExperience(false, true, !prevProps.shouldProceedQueryForGetHelp);
      }
    }

    if (this.props.isFeedbackCoachmarkEnabled && (prevState.coachmarkPosition !== this.state.coachmarkPosition)) {
      this.setState({
        positioningContainerProps: this.getMemoizedPositioningContainerProps(this.state.coachmarkPosition),
      });
    }

  };

  public onQueryChange(event: any) {
    if (event) {
      const query = event.target.value;
      this.props.setContactSearchedQueryText(query, false);
      this.props.setOriginalQuery(query);
      this.props.setAutoSuggestFocusedIndex(-1);
      this.charUpdate(query);
      if (!!query && !this.props.isQueryChanged) {
        this.props.setIsQueryChanged(true);
      }
      if (this.props.isAutoSuggestV2Enabled && query &&
        AutoSuggestV2Constants.characterMinimum < query.trim().length && query.trim().length < AutoSuggestV2Constants.characterMaximum) {
        // does not append wildcard if chinese, japanese, or thai language
        this.props.dispatchFetchAutoSuggestV2(query.trim() + (this.nonWildcardLanguages.indexOf(this.language) > -1 ? '' : '*'));
      } else if (this.props.isAutoSuggestEnabled && query &&
        AutoSuggestConstants.characterMinimum < query.trim().length && query.trim().length < AutoSuggestConstants.characterMaximum) {
        this.props.dispatchFetchAutoSuggest(query.trim() + '*');
      } else if (!this.props.isAutoSuggestHidden && query.trim().length < AutoSuggestConstants.characterMinimum) {
        this.props.hideAutoSuggest();
      }
      this.props.hideRequestCreationTab(true);
    }
  }

  private getMemoizedPositioningContainerProps(directionalHint: DirectionalHint) {
    return {
      directionalHint,
      doNotLayer: true,
    };
  }

  public isGetHelpButtonDisabled(): boolean {
    return !(
      this.props.contactSearchedQuery.length <= this.maxCharacterCount &&
      this.props.contactSearchedQuery.trim() !== '' &&
      !this.isIssueDescriptionTextAreaDisabled()
    );
  }

  public initiateGetHelpExperience(isMouseClick: boolean, deepLinkPathSearch?: boolean, firstSearchPerLaunch?: boolean) {
    let searchText = this.props.contactSearchedQuery;
    let actualQuery: string;
    const isSpecialQuery = false;
    let byPassPreProcess = false;
    if (!!deepLinkPathSearch) {
      const queries = this.queryWithDeeplinkPath(this.props.deeplinkPath);
      searchText = queries.searchText;
      actualQuery = queries.actualQuery;
      byPassPreProcess = !!actualQuery;
    }
    if (!searchText) {
      return;
    }
    if (!firstSearchPerLaunch) {
      appContext.sessionId = getRandomGuidString();
    }
    if (!deepLinkPathSearch) {
      getHelpLogger.logOnSearch(this.props.autoSuggestResults?.length > 0 || this.props.autoSuggestResultsV2?.suggestions?.length > 0, false);
    }
    collectTelemetry({
      ContentId: 'FetchSelfHelpSolutions', Type: LogType.PageAction, Hint: LogHint.FetchSelfHelpSolutions,
      Details: {
        'isMouseClick': !!isMouseClick, 'isDeepLinkGetHelp': !!deepLinkPathSearch, 'isFirstSearchPerLaunch': !!firstSearchPerLaunch,
        'isDeepLinkSmc': !!this.props.isDeepLinkQuery, 'isAutoSuggestQuery': !!this.props.isAutoSuggestQuery
      }
    });
    this.props.formSubmission(searchText);
    !!this.props.isAutoSuggestV2Enabled
      ? this.props.setIsAutoSuggestCardSearchV2(false, this.props.autoSuggestResultsV2)
      : this.props.setIsAutoSuggestCardSearch(false, this.props.autoSuggestResults);
    this.props.clearRelatedCorrelationIds();
    this.props.dispatchAlchemyFetch(searchText, actualQuery, isSpecialQuery, byPassPreProcess);
    this.props.setAutoSuggestResult([]);
    this.props.setAutoSuggestResultV2({ suggestions: [], searchText: [] });
    this.props.initializeModalityStateOnGetHelp(null);
    this.props.setOriginalQuery(searchText);
    this.props.setVsbUpsellAlertActivated();
    const queryIndex = this.props.suggestions?.length < 1 ? -1 : this.props.autoSuggestFocusedIndex;

    if (!isMouseClick) {
      const searchOverrideTags = {
        behavior: 160,
        content: {
          contentId: 'getHelp',
          areaName: 'formQuerySubmit',
          contentName: 'submitButton',
          formnm: 'maxFunnel',
          selfhelpprovider: 'alchemy',
          srchq: this.props.contactSearchedQuery.trim(),
          contactusfields: buildAutoSuggestContactUsFields(
            this.props.isDeepLinkQuery,
            this.props.isAnyAutoSuggestEnabled,
            this.props.originalQuery,
            this.props.suggestions,
            this.props.isAutoSuggestQuery,
            queryIndex,
            this.props.alchemyThemeRoutingEnabled
          ),
        },
        actionType: 'KE',
      };

      capturePageAction(null, searchOverrideTags);
    }
    this.props.setAutoSuggestFocusedIndex(-1);
    this.props.hideAutoSuggest();
    this.props.hideRequestCreationTab(false);
  }

  public PrivacyPolicy() {
    const articleUrl: string = 'https://go.microsoft.com/fwlink/?LinkID=507380';
    const linkStyles = getLinkStyles();
    return (
      <Link
        styles={linkStyles}
        id="privacyPolicyLink"
        data-bi-area="formQuerySubmit"
        data-bi-name="privacyPolicyLink"
        data-bi-formnm="maxFunnel"
        href={articleUrl}
        target="_blank"
      >
        {getString('search_privacyPolicyText')}
      </Link>
    );
  }

  private getCharactersRemaining = (query: string) => {
    return `${(this.maxCharacterCount - (!!query ? query.length : 0)).toString()} ${getString("search_charRemainingText")}`;
  };

  private getAriaLabel = () => {
    return `${this.getCharactersRemaining(this.props.contactSearchedQuery)}, ${getString('search_socContactUsFormAriaLabel')}, ${!this.props.isAutoSuggestHidden ? getString('result_autoSuggestOpenAriaLabel') : getString('result_autoSuggestHiddenAriaLabel')}`
  }

  public getSearchBar = () => {
    const searchBoxStyles = this.props.isGetHelp
      ? getGetHelpSearchBoxStyle(this.props.theme)
      : getSearchBoxStyles({ 
          theme: this.props.theme, 
          isTeams: this.props.isTeams,
          isSurfaceApp: this.props.isSurfaceApp
        });
    return (
      <SearchBox
        underlined={this.props.isGetHelp}
        maxLength={this.maxCharacterCount}
        styles={searchBoxStyles}
        value={this.props.contactSearchedQuery}
        onChange={e => this.onQueryChange(e)}
        onClear={this.onClear}
        onFocus={() => this.onFocus()}
        onBlur={e => this.onBlur(e)}
        ref={this.inputBoxRef}
        id="search-box-description-id"
        data-automation-id={AutomationIds.SearchHelpInput}
        disabled={this.isIssueDescriptionTextAreaDisabled()}
        placeholder={
          isSignUp() ? getString('search_signUpPagePlaceholderTextV2') : getString('search_socContactUsFormPlaceholder')
        }
        ariaLabel={this.getAriaLabel()}
        clearButtonProps={{ title: getString('search_clearButtonTitleText') }}
        onKeyDown={e => {
          if (
            this.props.isAnyAutoSuggestEnabled &&
            this.props.suggestions?.length > 0 &&
            (e.key === 'ArrowUp' || e.key === 'ArrowDown')
          ) {
            this.handleAutoSuggestArrowKeys(e);
          }
          if (
            e.key === 'Enter' &&
            this.props.contactSearchedQuery.length <= this.maxCharacterCount &&
            this.props.contactSearchedQuery.trim() !== ''
          ) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          } else if (e.key === 'Escape') {
            this.props.hideAutoSuggest();
          }
        }}
        onKeyUp={e => {
          if (
            e.key === 'Enter' &&
            this.props.contactSearchedQuery.length <= this.maxCharacterCount &&
            this.props.contactSearchedQuery.trim() !== ''
          ) {
            e.preventDefault();
            e.stopPropagation();
            if (!this.props.isGetHelpBtnHidden) {
              document.getElementById('getHelp').focus();
            }
            this.initiateGetHelpExperience(false);
            return false;
          }
        }}
      />
    );
  };

  public render() {
    const charRemaining = this.maxCharacterCount - this.props.contactSearchedQuery?.length;
    const willShowAutoSuggest = (this.props.isAnyAutoSuggestEnabled && !this.props.isAutoSuggestHidden && 0 < this.props.suggestions?.length && this.props.contactSearchedQuery);
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme, isDarkTheme: this.props.isDarkTheme });
    const getHelpButtonStyle = getHelpButtonStyles({ theme: this.props.theme });
    const queryIndex = this.props.suggestions?.length < 1 ? -1 : this.props.autoSuggestFocusedIndex;
    return (
      <div className={this.props.isGetHelpBtnHidden ? contactUsClassNames.extraPaddingBottomOnSectionHeader : undefined}>
        <div tabIndex={-1} className={mergeStyles(contactUsClassNames.row, 'form-group', contactUsClassNames.noGutter)}>
          <div tabIndex={-1} className={mergeStyles('col-lg-10', contactUsClassNames.col)}>
            {this.props.showSearchBar ? this.getSearchBar() : this.getSearchTextArea()}
            {
              willShowAutoSuggest &&
              <SelfHelpAutoSuggest
                inputBoxRef={this.inputBoxRef}
                hideRequestCreationTab={this.props.hideRequestCreationTab}
              />
            }
            {this.props.isFeedbackCoachmarkShowing &&
              <Coachmark
                target={this.inputBoxRef.current}
                positioningContainerProps={this.state.positioningContainerProps}
                ariaAlertText="A coachmark has appeared"
                ariaDescribedByText="Press enter or alt + C to open the coachmark notification"
                ariaLabelledByText="Coachmark notification"
              >
                <TeachingBubbleContent
                  headline={getString("coachMarkHeader_text")}
                  hasCloseButton
                  closeButtonAriaLabel="Close"
                  onDismiss={() => {
                    this.props.hideCoachmark()}
                  }
                >
                  {getString("coachMark_text")}
                </TeachingBubbleContent>
              </Coachmark>}
          </div>
          <div className={mergeStyles('col-lg-10', contactUsClassNames.caption, 'text-counter', contactUsClassNames.col)}>
            <span
              id="issueDescRemainChar"
              ref={(node) => (this.charRemainingSpan = node)}
              role="status"
              aria-label={this.props.contactSearchedQuery}
            >
              {charRemaining + ' ' + getString("search_charRemainingText")}
            </span>
            {this.getPrivacyAndSupportPolicy()}
          </div>
        </div>
        {!this.props.isGetHelpBtnHidden && (<div tabIndex={-1} className={mergeStyles(contactUsClassNames.row, 'col-lg-10', contactUsClassNames.col)}>
          <PrimaryButton
            data-bi-bhvr="CONTACT"
            data-bi-area="formQuerySubmit"
            data-bi-name="submitButton"
            data-bi-formnm="maxFunnel"
            data-bi-selfhelpprovider="alchemy"
            data-bi-srchq={this.props.contactSearchedQuery?.trim()}
            data-bi-contactusfields={buildAutoSuggestContactUsFields(
              this.props.isDeepLinkQuery,
              this.props.isAnyAutoSuggestEnabled,
              this.props.originalQuery,
              this.props.suggestions,
              this.props.isAutoSuggestQuery,
              queryIndex,
              this.props.alchemyThemeRoutingEnabled
            )}
            onClick={(e) => {
              const appState: IAppState = store.getState();
              const sourceApp: SourceApps = appState.GlobalState.sourceApp;
              if (!!sourceApp && (sourceApp.toLowerCase() === SourceApps.SurfaceApp)) {
                if (window && window.chrome && window.chrome.webview && window.chrome.webview.postMessage) {
                  window.chrome.webview.postMessage("GetHelpButtonClicked");
                }
              }
              // use clientX to identify whether it is the user click event or the manual triggered click in componentDidMount
              this.initiateGetHelpExperience(e.clientX > 0);
            }}
            id="getHelp"
            data-automation-id={AutomationIds.GetHelpBtn}
            styles={getHelpButtonStyle}
            text={
              getString('search_getHelpButtonText')
            }
            disabled={this.isGetHelpButtonDisabled()}
          />
        </div>)}
      </div>
    );
  }

  private charUpdate(query: string) {
    this.charRemainingSpan.textContent = this.getCharactersRemaining(query);
  }

  private onBlur(e: any) {
    // hide if user clicks away from textbox and autosuggest elements
    const elm = e.relatedTarget as HTMLElement;
    if (this.props.isAutoSuggestV2Enabled && !this.props.isAutoSuggestHidden &&
      (!elm || !!elm && !(elm.className.indexOf(AutoSuggestV2Constants.cssClassName) !== -1))) {
      this.props.hideAutoSuggest();
    } else if (this.props.isAutoSuggestEnabled && !this.props.isAutoSuggestHidden &&
      (!elm || !!elm && !(elm.className.indexOf(AutoSuggestConstants.cssClassName) !== -1))) {
      this.props.hideAutoSuggest();
    }
    if (!this.props.contactSearchedQuery) {
      this.props.setIsQueryChanged(false);
    }
  }

  private onFocus() {
    if (this.props.isAnyAutoSuggestEnabled && this.props.isAutoSuggestHidden && 0 < this.props.suggestions?.length) {
      this.props.showAutoSuggest();
    }
  }

  private isIssueDescriptionTextAreaDisabled(): boolean {
    return this.props.isAlchemyQueryInProgress || this.props.isCreatingRequest;
  }

  private handleAutoSuggestArrowKeys(e: React.KeyboardEvent) {
    let index: number;
    if (e.key === 'ArrowUp') {
      // arrow up
      index = this.props.autoSuggestFocusedIndex - 1 >= -1 ? this.props.autoSuggestFocusedIndex - 1 : this.props.suggestions?.length - 1;
    } else if (e.key === 'ArrowDown') {
      // arrow down
      index = this.props.autoSuggestFocusedIndex + 1 < this.props.suggestions?.length ? this.props.autoSuggestFocusedIndex + 1 : -1;
    }
    this.props.setAutoSuggestFocusedIndex(index);
    if (index === -1) {
      // if index is -1, display user input.
      this.props.setContactSearchedQueryText(this.props.originalQuery, false);
    } else {
      !!this.props.isAutoSuggestV2Enabled
        ? this.props.setContactSearchedQueryText(this.props.autoSuggestResultsV2.suggestions[index], true)
        : this.props.setContactSearchedQueryText(this.props.autoSuggestResults[index]['@search.text'], true);
    }
    e.preventDefault();
    e.stopPropagation();
  }

  private SupportPolicy = () => (
    <Link
      styles={getLinkStyles()}
      id="supportPolicyLink"
      data-bi-area="formQuerySubmit"
      data-bi-name="supportPolicyLink"
      data-bi-formnm="maxFunnel"
      href={`/${this.props.market}/article/` + '96162163-b3aa-498b-bbbb-5e757b0f31da'}
      target="_blank"
    >
      {getString('search_supportPolicyText')}
    </Link>
  );

  private onClear = (_event: React.ChangeEvent<HTMLInputElement>) => {
    getHelpLogger.logClearingSearchBox();
    this.props.setContactSearchedQueryText('', false);
    this.props.setOriginalQuery('');
    this.props.setAutoSuggestFocusedIndex(-1);
    this.charUpdate('');
    this.props.hideAutoSuggest();
    this.props.hideRequestCreationTab(true);
  };

  private getSearchTextArea = () => {
    const commonClassNames = getCommonClassNames(getCommonStyles, {});
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme });

    return (
      <TextField
        maxLength={this.maxCharacterCount}
        value={this.props.contactSearchedQuery}
        onChange={e => this.onQueryChange(e)}
        onFocus={() => this.onFocus()}
        onBlur={e => this.onBlur(e)}
        componentRef={this.inputBoxRef}
        id="issue-description-id"
        disabled={this.isIssueDescriptionTextAreaDisabled()}
        styles={{ field: contactUsClassNames.issueDescription }}
        className={this.props.showCompactQueryForm ? commonClassNames.resizeNone : commonClassNames.resizeBoth}
        multiline={!this.props.showCompactQueryForm}
        resizable={false}
        rows={this.props.showCompactQueryForm ? 1 : 3}
        placeholder={
          isSignUp()
            ? getString('search_signUpPagePlaceholderText')
            : getString('search_socContactUsFormPlaceholder')
        }
        ariaLabel={this.getAriaLabel()}
        autoFocus={true}
        onKeyDown={e => {
          if (
            this.props.isAnyAutoSuggestEnabled &&
            this.props.suggestions?.length > 0 &&
            (e.key === 'ArrowUp' || e.key === 'ArrowDown')
          ) {
            this.handleAutoSuggestArrowKeys(e);
          }
          if (
            e.key === 'Enter' &&
            this.props.contactSearchedQuery.length <= this.maxCharacterCount &&
            this.props.contactSearchedQuery.trim() !== ''
          ) {
            e.preventDefault();
            e.stopPropagation();
            return false;
          } else if (e.key === 'Escape') {
            this.props.hideAutoSuggest();
          }
        }}
        onKeyUp={e => {
          if (
            e.key === 'Enter' &&
            this.props.contactSearchedQuery.length <= this.maxCharacterCount &&
            this.props.contactSearchedQuery.trim() !== ''
          ) {
            e.preventDefault();
            e.stopPropagation();
            if (!this.props.isGetHelpBtnHidden) {
              document.getElementById('getHelp').focus();
            }
            this.initiateGetHelpExperience(false);
            return false;
          }
        }}
      />
    );
  };

  private getPrivacyAndSupportPolicy = (): React.ReactElement => {
    if (!this.props.isPrivacyAndSupportHidden) {
      const privacyPolicyTag: React.ReactElement = this.PrivacyPolicy();
      const fullRightClassName = getPullRightClassName(isRtl());
      if (!this.props.isThirdParty) {
        const supportPolicyTag: React.ReactElement = this.SupportPolicy();
        return (
          <span className={fullRightClassName}>
            {privacyPolicyTag} | {supportPolicyTag}
          </span>
        );
      } else {
        return <span className={fullRightClassName}>{privacyPolicyTag}</span>;
      }
    }
    return null;
  };

  private queryWithDeeplinkPath = (deeplinkPath: string) => {
    // Todo: Brook - add check for gethelp config change
    // Todo: Brook - add logic to update contactSearchedQuery Text and auto suggest dropdown - basically intiateGetHelpExperience logic
    const [searchText, actualQuery] = getQueryFromDeepLink(deeplinkPath);
    return {
      searchText, actualQuery,
    };
  };
}
